import { FC, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	FormControlLabel,
	Stack,
	Switch,
	TextField,
	Typography
} from '@mui/material';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import IconButton from '@/components/@extended/IconButton';
import { PopupTransition } from '@/components/@extended/Transitions';

// ==============================|| DIALOG DUPLICARE (stil template) ||============================== //
// Dialog generic de duplicare a unei entități (anunț, consultare…): preumple titlul cu
// „{numeSursa} (copie)", opțional „Creează ca draft".

interface DuplicateDialogProps {
	open: boolean;
	/** Numele entității duplicate, la nominativ nearticulat (ex. „anunț", „consultare"). */
	titluEntitate?: string;
	/** Numele sursei; se preumple „{numeSursa} (copie)"; null → dialog inert. */
	numeSursa: string | null;
	onClose: () => void;
	onConfirm: (nume: string, asDraft: boolean) => void;
}

export const DuplicateDialog: FC<DuplicateDialogProps> = ({
	open,
	titluEntitate = 'anunț',
	numeSursa,
	onClose,
	onConfirm
}) => {
	const [nume, setNume] = useState('');
	const [asDraft, setAsDraft] = useState(true);

	useEffect(() => {
		if (open && numeSursa) {
			setNume(`${numeSursa} (copie)`);
			setAsDraft(true);
		}
	}, [open, numeSursa]);

	if (!numeSursa) return null;

	const canSubmit = nume.trim().length > 0;

	const submit = (): void => {
		if (!canSubmit) return;
		onConfirm(nume.trim(), asDraft);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			slots={{ transition: PopupTransition }}
			aria-labelledby="duplicate-title"
		>
			<Box sx={{ position: 'relative', p: 2.5, pb: 0 }}>
				<Typography variant="h4" id="duplicate-title">
					Duplică {titluEntitate}
				</Typography>
				<IconButton
					color="secondary"
					onClick={onClose}
					sx={{ position: 'absolute', top: 12, right: 12 }}
					aria-label="Închide"
				>
					<CloseOutlined />
				</IconButton>
			</Box>

			<DialogContent sx={{ p: 2.5 }}>
				<Stack sx={{ gap: 2.5 }}>
					<Typography variant="body2" sx={{ color: 'text.secondary' }}>
						Se creează o copie după{' '}
						<Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
							„{numeSursa}”
						</Box>
						.
					</Typography>

					<TextField
						label="Titlu nou"
						value={nume}
						onChange={(e) => setNume(e.target.value)}
						fullWidth
						autoFocus
						onKeyDown={(e) => {
							if (e.key === 'Enter') submit();
						}}
					/>

					<FormControlLabel
						control={
							<Switch checked={asDraft} onChange={(e) => setAsDraft(e.target.checked)} />
						}
						label="Creează ca draft"
					/>

					<Stack direction="row" sx={{ gap: 1.5, justifyContent: 'flex-end' }}>
						<Button color="secondary" variant="outlined" onClick={onClose}>
							Renunță
						</Button>
						<Button
							variant="contained"
							startIcon={<CopyOutlined />}
							disabled={!canSubmit}
							onClick={submit}
						>
							Duplică
						</Button>
					</Stack>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

DuplicateDialog.defaultProps = { titluEntitate: 'anunț' };
